import React, { ReactElement } from "react";
import { ExerciseImages, ExerciseImagesMobile, Exercises, ExerciseTypes } from "../../../../enums/exercises";
import { UT } from "@transifex/react";
import useExerciseTitle from "../../../../hooks/useExerciseTitle";
import { ExerciseName } from "../StyledComponents";

import {
    ExerciseWrapper,
    ExerciseTitleAndInfoWrapper,
    ExerciseNameQuestionmarkWrapper,
    ExerciseInfo,
    ExerciseQuestionMarkWrapper,
    StyledQuestionMarkIcon,
    ExerciseDescriptionWrapper,
    ExerciseTitleSideWrapper,
    ExerciseImageSideWrapper,
    ExerciseTypeImage,
    ExerciseTypeImageWrapper,
} from "./StyledComponents";
import PhaseSixIcon from "../../../../basic/phaseSixIcon/PhaseSixIcon";

const getImageSource = (exerciseType: Exercises) => {
    return ExerciseImages.get(exerciseType);
};

const getMobileImageSource = (exerciseType: Exercises) => {
    return ExerciseImagesMobile.get(exerciseType);
};

const getDescription = (exerciseType: Exercises): ReactElement | string => {
    switch (exerciseType) {
        case Exercises.STANDARD:
            return (
                <UT
                    _str="In the <b>standard</b> exercise, the words and their sole meaning are going to be asked. The students will fill in the gap with the translation, either on the target language or base language or randomly. "
                    _inline
                    _tags="CreateTest,SelectExerciseType"
                />
            );
        case Exercises.CONNECT_WORDS:
            return (
                <UT
                    _str="In this exercise the students have to <b>connect words</b> that are in two columns, each of five words. Like the Standard exercise, this exercise allows the practice of the definition of the words."
                    _inline
                    _tags="CreateTest,SelectExerciseType"
                />
            );
        case Exercises.FILL_GAP:
            return (
                <UT
                    _str="In this exercise, the example phrases from the book will be shown as <b>gap sentences</b>. The words that belong in the gap, are located in a box on top of the sentences. The students need to translate the words and find the right context. <b>Info:</b> It is possible that in your selected Book, there are not or only a few gap sentences available."
                    _inline
                    _tags="CreateTest,SelectExerciseType"
                />
            );
        case Exercises.WRITE_SENTENCE:
            return (
                <UT
                    _str="In this exercise, students receive an <b>individual input field for each selected word</b>. This allows grammar checks (e.g. conjugations or comparative forms), semantic knowledge tests (e.g. synonyms or antonyms), or sentence building practice."
                    _inline
                    _tags="CreateTest,SelectExerciseType"
                />
            );
        case Exercises.CREATE_TEXT:
        case Exercises.FREE_TEXT:
            return (
                <UT
                    _str="In this exercise, students write a <b>coherent text</b>. Optionally, selected vocabulary or a hint can be added. This makes the Text task suitable for guided writing as well as free, book-independent exercises or instructions between exercises."
                    _inline
                    _tags="CreateTest,SelectExerciseType"
                />
            );
        default:
            return "";
    }
};

export interface SelectExerciseTypeItemProps {
    exerciseType: Exercises;
    description?: string;
    infoText?: string;
    isDisabled?: boolean;
    isInfoHidden?: boolean;
    isInfoOpen: boolean;
    onToggleInfo: (event: React.MouseEvent<HTMLDivElement>) => void;
    onSelect: () => void;
}

const SelectExerciseTypeItem: React.FC<SelectExerciseTypeItemProps> = (props) => {
    const { getExerciseNameFromQuestionMode } = useExerciseTitle();

    const renderExerciseDescription = () => {
        return (
            <ExerciseDescriptionWrapper disabled={props.isDisabled}>
                {props.description || getDescription(props.exerciseType)}
            </ExerciseDescriptionWrapper>
        );
    };

    const renderExerciseNameAndQuestionMark = () => {
        const questionModeExerciseType = ExerciseTypes.get(props.exerciseType);
        return (
            <ExerciseNameQuestionmarkWrapper>
                <ExerciseName className={props.isInfoOpen ? "infoOpen" : ""}>
                    {getExerciseNameFromQuestionMode(questionModeExerciseType)}
                </ExerciseName>
                {!props.isInfoHidden && (
                    <ExerciseQuestionMarkWrapper
                        onClick={(event) => props.onToggleInfo(event)}
                        className={props.isInfoOpen ? "infoOpen" : ""}
                    >
                        <StyledQuestionMarkIcon className={props.isInfoOpen ? "infoOpen" : ""}>
                            <PhaseSixIcon name="help" />
                        </StyledQuestionMarkIcon>
                    </ExerciseQuestionMarkWrapper>
                )}
            </ExerciseNameQuestionmarkWrapper>
        );
    };

    const renderTitleAndInfo = () => {
        return (
            <ExerciseTitleAndInfoWrapper
                className={props.isInfoOpen ? "infoOpen" : ""}
                disabled={props.isDisabled}
            >
                {renderExerciseNameAndQuestionMark()}
                {props.infoText && <ExerciseInfo>{props.infoText}</ExerciseInfo>}
            </ExerciseTitleAndInfoWrapper>
        );
    };

    const renderExerciseImage = () => {
        return (
            <ExerciseTypeImageWrapper
                className={props.isInfoOpen ? "infoOpen" : ""}
                disabled={props.isDisabled}
            >
                <picture>
                    <source
                        media={`(max-width: ${process.env.REACT_APP_LOWER_GRID_STEP_BORDER}px)`}
                        srcSet={getMobileImageSource(props.exerciseType)}
                    />
                    <ExerciseTypeImage src={getImageSource(props.exerciseType)} />
                </picture>
            </ExerciseTypeImageWrapper>
        );
    };

    return (
        <ExerciseWrapper
            data-testid={props.exerciseType}
            className={props.isInfoOpen ? "infoOpen" : ""}
            onClick={props.onSelect}
            disabled={props.isDisabled}
        >
            <ExerciseTitleSideWrapper className={props.isInfoOpen ? "infoOpen" : ""}>
                {renderTitleAndInfo()}
                {props.isInfoOpen && renderExerciseDescription()}
            </ExerciseTitleSideWrapper>
            <ExerciseImageSideWrapper className={props.isInfoOpen ? "infoOpen" : ""}>
                {renderExerciseImage()}
            </ExerciseImageSideWrapper>
        </ExerciseWrapper>
    );
};

export default SelectExerciseTypeItem;
